<template>
  <div>
    <label class="block mb-2">
      {{ t('templateBuilder.fields.htmlCode') }}
    </label>
    <!-- CodeMirror's hidden textarea must not reach the panel's @input fallthrough handler -->
    <div
      class="codemirror-wrapper"
      @input.stop
    >
      <div class="codemirror-header">
        <i
          class="fas fa-expand-alt expand-code-btn"
          :title="t('templateBuilder.codeEditor.expandToModal')"
          @click="showCodeModal = true"
        />
      </div>
      <VCodeMirror
        ref="codeMirrorRef"
        v-model:value="inputValue"
        :mode="{ name: 'text/html' }"
        :wrap="true"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>
    <CodeMirrorModal
      v-model:visible="showCodeModal"
      v-model:code="inputValue"
      language="html"
      :title="t('templateBuilder.fields.customHtml.modalTitle')"
      :show-variables="variablesList.length > 0"
      :variables-list="variablesList"
      @apply="handleModalApply"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { VCodeMirror } from 'vue3-code-mirror';
import 'codemirror/mode/htmlmixed/htmlmixed';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';
import type { Translation } from '@/types';
import type { Variable } from '@/components/template-builder/utils/variables-list';

// Raw HTML translation editor: unlike the rich text editor, it keeps tables and inline styles untouched
export default defineComponent({
  name: 'BaseHtmlCodeEditor',

  components: {
    VCodeMirror,
    CodeMirrorModal,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'activate-group', 'deactivate-group'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const codeMirrorRef = ref();
    const inputValue = ref(props.modelValue);
    const showCodeModal = ref(false);
    const isFocused = ref(false);
    // Last value received from or sent to the parent: only a user edit differs from it
    let syncedValue = inputValue.value;

    const syncFromParent = () => {
      syncedValue = props.modelValue;
      inputValue.value = props.modelValue;
    };

    const emitIfEdited = () => {
      if (inputValue.value === syncedValue) return false;
      syncedValue = inputValue.value;
      emit('update:modelValue', inputValue.value);
      return true;
    };

    const focus = () => {
      if (codeMirrorRef.value?.editor) codeMirrorRef.value.editor.focus();
    };

    const handleFocus = () => {
      isFocused.value = true;
      if (props.translation) emit('activate-group', props.translation.groupKey);
    };

    const handleBlur = () => {
      isFocused.value = false;
      // A value set by the parent while focused (AI translation) must not be overwritten
      if (!emitIfEdited()) syncFromParent();
      if (props.translation) emit('deactivate-group', props.translation.groupKey);
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      emitIfEdited();
    };

    watch(() => props.modelValue, () => {
      if (!isFocused.value) syncFromParent();
    });

    onMounted(() => {
      if (codeMirrorRef.value?.editor) codeMirrorRef.value.editor.setOption('theme', 'default');
    });

    onBeforeUnmount(() => {
      emitIfEdited();
      if (codeMirrorRef.value) codeMirrorRef.value.cleanEvent();
    });

    return {
      t,
      codeMirrorRef,
      inputValue,
      showCodeModal,
      focus, // eslint-disable-line vue/no-unused-properties
      handleFocus,
      handleBlur,
      handleModalApply,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .CodeMirror {
  height: 300px !important;
  font-size: 12px;
}

.codemirror-wrapper {
  position: relative;
  width: 100%;
}

.codemirror-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;

  .expand-code-btn {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
    color: #607D8B;
    font-size: 12px;

    &:hover {
      background: #f8f9fa;
      color: #495057;
    }
  }
}
</style>
